/**
 * Campo de busca com a lupa à esquerda, para o filtro de uma lista.
 *
 * Papel: um `Input` de tipo `search` com o ícone posicionado dentro do campo,
 * limitado a 380 px de largura, sobre a superfície clara em vez do fundo mais
 * escuro dos demais campos de formulário.
 *
 * Como usar: as props de `<input>` — `value`, `onChange`, `placeholder`,
 * `id` — são repassadas direto para o campo interno, então quem chama usa
 * este componente exatamente como usaria `Input`. O rótulo continua sendo
 * responsabilidade de quem chama, com `Label` e `htmlFor` apontando para o
 * `id` passado aqui.
 */
import { type ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';
import { Input } from '@/shared/ui/input';

/** Atributos do ícone de lupa, à esquerda do campo. */
const ATRIBUTOS_DA_LUPA = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * O campo de busca.
 *
 * @param className - Classes de quem chama, aplicadas ao campo interno depois
 *   das internas pelo `cn()` e por isso capazes de sobrescrevê-las.
 * @param props - Demais atributos de `<input>`, repassados ao campo interno.
 * @returns O campo com a lupa posicionada à esquerda.
 */
export function CampoDeBusca({ className, ...props }: ComponentProps<typeof Input>) {
  return (
    <div className="relative mb-5 max-w-[380px]">
      <svg {...ATRIBUTOS_DA_LUPA} className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-texto-fraco">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <line x1="15.3" y1="15.3" x2="20.5" y2="20.5" />
      </svg>
      <Input type="search" className={cn('bg-superficie pl-[38px]', className)} {...props} />
    </div>
  );
}
